import React from 'react';
import { Tag } from '../general/Tag';

export interface Column {
  key: string;
  title: string;
  dataIndex: string;
  width?: number;
  textAlign?: 'left' | 'center' | 'right';
  borderRight?: boolean;
  render?: (value: any, record: any, index: number) => React.ReactNode;
}

export interface DataSource {
  key: string;
  data: { [key: string]: any };
  isSeparator?: boolean;
  separatorStyle?: 'H1' | 'H2' | 'H3' | 'H4';
  isCollapsible?: boolean;
  isExpanded?: boolean;
  aggregatedData?: { [key: string]: any };
  parentKey?: string;
  level?: number;
  defaultCollapsed?: boolean;
  showTag?: boolean;
  ignoreFromTagcount?: boolean;
  isClickable?: boolean;
  isFooter?: boolean;
}

export interface CollapsibleTableProps {
  columns: Column[];
  dataSource: DataSource[];
  bordered?: boolean;
  size?: 'small' | 'middle' | 'large';
  className?: string;
  width?: number | string;
  height?: number | string;
  scroll?: {
    x?: number | string;
    y?: number | string;
  };
  customHeader?: (columns: Column[]) => React.ReactNode;
  onRowClick?: (record: DataSource) => void;
}

const CollapsibleTable: React.FC<CollapsibleTableProps> = ({
  columns,
  dataSource,
  bordered = false,
  size = 'middle',
  className = '',
  width,
  height,
  scroll,
  customHeader,
  onRowClick,
}) => {
  const [expandedRows, setExpandedRows] = React.useState<{ [key: string]: boolean }>(() => {
    const initialState: { [key: string]: boolean } = {};
    dataSource.forEach(item => {
      if (item.isCollapsible) {
        initialState[item.key] = item.defaultCollapsed === false;
      }
    });
    return initialState;
  });

  React.useEffect(() => {
    setExpandedRows(prev => {
      const next: { [key: string]: boolean } = {};
      dataSource.forEach(item => {
        if (item.isCollapsible) {
          next[item.key] =
            Object.prototype.hasOwnProperty.call(prev, item.key)
              ? prev[item.key]
              : item.defaultCollapsed === false;
        }
      });

      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (
        prevKeys.length === nextKeys.length &&
        nextKeys.every(key => prev[key] === next[key])
      ) {
        return prev;
      }

      return next;
    });
  }, [dataSource]);

  const childCounts = React.useMemo(() => {
    const counts: { [key: string]: number } = {};
    dataSource.forEach(item => {
      if (item.parentKey && item.ignoreFromTagcount !== true) {
        counts[item.parentKey] = (counts[item.parentKey] || 0) + 1;
      }
    });
    return counts;
  }, [dataSource]);

  const sizeClasses = {
    small: 'text-sm',
    middle: 'text-base',
    large: 'text-lg',
  };

  const handleToggleCollapse = (key: string, isExpanded: boolean) => {
    setExpandedRows(prev => {
      const newState = { ...prev };
      newState[key] = isExpanded;

      if (!isExpanded) {
        const collapseChildren = (parentKey: string) => {
          dataSource.forEach(item => {
            if (item.parentKey === parentKey) {
              newState[item.key] = false;
              collapseChildren(item.key);
            }
          });
        };
        collapseChildren(key);
      }

      return newState;
    });
  };

  const filteredDataSource = dataSource.filter(record => {
    const isAnyParentCollapsed = (currentRecord: DataSource): boolean => {
      if (!currentRecord.parentKey) return false;

      const parent = dataSource.find(p => p.key === currentRecord.parentKey);
      if (!parent) return false;

      if (expandedRows && !expandedRows[parent.key]) {
        return true;
      }

      return isAnyParentCollapsed(parent);
    };

    return !isAnyParentCollapsed(record);
  });

  return (
    <div
      className={`relative ${className}`}
      style={{
        width: width || '100%',
        height: height || 'auto'
      }}
    >
      <div
        className="overflow-auto"
        style={{
          maxHeight: scroll?.y || height || 'auto',
          maxWidth: scroll?.x || width || 'auto',
        }}
      >
        <table
          className={`${className} min-w-full divide-y divide-gray-200 ${sizeClasses[size]} ${bordered ? 'border border-gray-200' : ''
            }`}
          style={{
            minWidth: scroll?.x || 'auto'
          }}
        >
          <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
            {customHeader ? (
              customHeader(columns)
            ) : (
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={column.key}
                    className={`px-4 py-3 text-sm font-semibold text-gray-900 border-b border-gray-200 bg-gray-50 ${index === 0 ? 'sticky left-0 z-20 text-left' : 'sticky top-0 z-10'} ${column.textAlign === 'center' ? 'text-center' : column.textAlign === 'right' ? 'text-right' : 'text-left'} ${column.borderRight ? 'border-r border-gray-200' : ''}`}
                    style={{
                      width: column.width,
                      minWidth: column.width,
                      maxWidth: column.width
                    }}
                  >
                    {column.title}
                  </th>
                ))}
              </tr>
            )}
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {filteredDataSource.map((record, index) => {
              const getSeparatorStyling = () => {
                if (!record.isSeparator) return { bg: '', text: '', font: '' };

                switch (record.separatorStyle) {
                  case 'H1':
                    return {
                      bg: 'bg-gray-100',
                      font: 'font-bold text-lg'
                    };
                  case 'H2':
                    return {
                      bg: 'bg-gray-50',
                      text: 'text-gray-800',
                      font: 'font-semibold text-base'
                    };
                  case 'H3':
                    return {
                      text: 'text-gray-600',
                      font: 'font-medium text-sm',
                      indent: true
                    };
                  case 'H4':
                    return {
                      bg: 'bg-gray-50',
                      text: 'text-gray-500',
                      font: 'font-medium text-xs'
                    };
                  default:
                    return {
                      bg: 'bg-gray-50',
                      text: 'text-gray-600',
                      font: 'font-medium text-sm'
                    };
                }
              };

              const separatorStyle = getSeparatorStyling();

              const isClickable = !!onRowClick && record.isClickable !== false;

              return (
                <tr
                  key={record.key}
                  className={`${record.isSeparator ? separatorStyle.bg : (record.isFooter ? 'bg-gray-50 font-semibold' : (isClickable ? 'hover:bg-gray-50' : ''))} ${isClickable ? 'cursor-pointer' : ''}`}
                  onClick={() => {
                    if (isClickable) {
                      onRowClick?.(record);
                    }
                  }}
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={column.key}
                      className={`px-4 py-3 whitespace-nowrap ${record.isSeparator
                          ? `${separatorStyle.bg} ${separatorStyle.text} ${separatorStyle.font}`
                          : (record.isFooter ? 'text-sm text-gray-900 bg-gray-50' : 'text-sm')
                        } ${colIndex === 0 && !record.isSeparator && !record.isFooter
                          ? 'bg-white sticky left-0 z-5 border-r border-gray-200'
                          : colIndex === 0 && (record.isSeparator || record.isFooter)
                            ? `${record.isSeparator ? separatorStyle.bg : 'bg-gray-50'} sticky left-0 z-5 border-r border-gray-200`
                            : ''
                        } ${column.textAlign === 'center' ? 'text-center' : column.textAlign === 'right' ? 'text-right' : ''} ${column.borderRight ? 'border-r border-gray-200' : ''}`}
                      style={{
                        width: column.width,
                        minWidth: column.width,
                        maxWidth: column.width,
                        paddingLeft: colIndex === 0 && (record?.level || 0) > 0
                          ? `${(record?.level || 0) * 16 + 16}px`
                          : undefined,
                      }}
                    >
                      {(() => {
                        if (record.isSeparator) {
                          if (colIndex === 0) {
                            if (record.isCollapsible) {
                              const childCount = childCounts[record.key] || 0;

                              return (
                                <div className="flex items-center">
                                  <button
                                    onClick={() => handleToggleCollapse(record.key, !expandedRows[record.key])}
                                    className="mr-2 p-1 hover:bg-gray-200 rounded"
                                  >
                                    {expandedRows[record.key] ? (
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    ) : (
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                    )}
                                  </button>
                                  <span>
                                    {record.data[column.dataIndex] ?? ''}
                                  </span>
                                  {childCount > 0 && record.showTag && (
                                    <Tag variant="primary" className="ml-2 copy-exclude">
                                      {childCount}
                                    </Tag>
                                  )}
                                </div>
                              );
                            } else {
                              return (
                                <span>
                                  {record.data[column.dataIndex] ?? ''}
                                </span>
                              );
                            }
                          }
                          return '';
                        } else if (record.isCollapsible) {
                          if (colIndex === 0) {
                            const childCount = childCounts[record.key] || 0;

                            return (
                              <div className="flex items-center">
                                <button
                                  onClick={() => handleToggleCollapse(record.key, !expandedRows[record.key])}
                                  className="mr-2 p-1 hover:bg-gray-200 rounded"
                                >
                                  {expandedRows[record.key] ? (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  )}
                                </button>
                                <span className="text-sm font-medium text-gray-900">
                                  {record.data[column.dataIndex] ?? ''}
                                </span>
                                {childCount > 0 && record.showTag && (
                                  <Tag variant="primary" className="ml-2 copy-exclude">
                                    {childCount}
                                  </Tag>
                                )}
                              </div>
                            );
                          } else if (expandedRows[record.key]) {
                            return '';
                          } else {
                            return record.aggregatedData && record.aggregatedData[column.dataIndex] !== undefined
                              ? (column.render
                                ? column.render(record.aggregatedData[column.dataIndex], record, index)
                                : record.aggregatedData[column.dataIndex])
                              : '';
                          }
                        } else {
                          if (colIndex === 0) {
                            return (
                              <span className="text-sm font-medium text-gray-900">
                                {column.render
                                  ? column.render(record.data[column.dataIndex], record, index)
                                  : record.data[column.dataIndex] ?? ''}
                              </span>
                            );
                          } else {
                            return column.render
                              ? column.render(record.data[column.dataIndex], record, index)
                              : record.data[column.dataIndex] ?? '';
                          }
                        }
                      })()}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CollapsibleTable;